import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import feedback from "../../../assets/courses/feedback.jpg";
import grenzen from "../../../assets/courses/grenzen.jpg";
import lesstart from "../../../assets/courses/lesstart.jpg";
import onrust from "../../../assets/courses/onrust.jpg";
import oudergesprek from "../../../assets/courses/oudergesprek.jpg";
import type { SituationCardCopy, SituationCardSection } from "../pages/situation-card-view.js";
import { SituationGroundedness } from "./SituationGroundedness.js";
import { SituationCardSections, type SectionRevision } from "./SituationCardSections.js";

const courseThumbnails = [oudergesprek, lesstart, onrust, grenzen, feedback];

export function SituationCardPreview({
  title,
  activity = "steady",
  dropping,
  paragraphs,
  references = {},
  groundedness,
  revisions,
  onStreamDone,
  courses,
  onDragLeave,
  onDragOver,
  onDrop,
}: {
  title: string;
  activity?: "steady" | "loading" | "streaming" | "settled";
  dropping: boolean;
  paragraphs: SituationCardCopy;
  references?: Partial<Record<SituationCardSection, number[]>>;
  groundedness: number;
  revisions?: Partial<Record<SituationCardSection, SectionRevision>>;
  onStreamDone?: () => void;
  courses: Array<{ id: string; title: string }>;
  onDragLeave: (event: React.DragEvent<HTMLElement>) => void;
  onDragOver: (event: React.DragEvent<HTMLElement>) => void;
  onDrop: (event: React.DragEvent<HTMLElement>) => void;
}) {
  const { t } = useTranslation();
  return (
    <section
      aria-busy={activity === "loading" || activity === "streaming"}
      aria-label={title}
      className={`scrollbar-none min-h-0 overflow-y-auto ${dropping ? "bg-primary/5" : ""}`}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div className="space-y-6">
        <p
          className="flex items-start gap-3 rounded-xl bg-info px-4 py-3 text-sm leading-relaxed text-info-foreground"
          role="note"
        >
          <Info aria-hidden="true" className="mt-0.5 size-5 shrink-0" />
          <span>{t("administration.situationCard.contextBanner")}</span>
        </p>
        <SituationGroundedness percent={groundedness} />
        <SituationCardSections
          mode={activity}
          onStreamDone={onStreamDone}
          paragraphs={paragraphs}
          references={references}
          revisions={activity === "steady" ? revisions : undefined}
        />
        <ul className="space-y-3">
          {courses.map((course, index) => (
            <li className="flex items-center gap-4 rounded-2xl border border-border bg-background p-3" key={course.id}>
              <img
                alt=""
                className="h-16 w-28 shrink-0 rounded-lg object-cover"
                src={courseThumbnails[index % courseThumbnails.length]}
              />
              <div className="min-w-0">
                <h3 className="text-base font-medium text-foreground">{course.title}</h3>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {t("administration.situationCard.recommendedForSituation")}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

